import React from 'react';
import { MenuOption } from 'models/utils/common/menu/menu-option-model';
import moment from 'moment';
import { DATE_FORMATS } from 'constants/util/date-constants';
import DataGridColumn, {
  DataGridCheckboxColumn,
} from 'models/utils/common/data-grid/data-grid-column-model';
import MultilineEllipsis from '../../components/common/multi-line-ellipsis-text-component';
import { TooltipOptions } from 'models/utils/common/tooltip-options-model';
import { FormattedMessage } from 'react-intl';
import DATA_GRID_CONSTANTS from 'constants/common/data-grid-constants';

const { SPECIAL_COLUMNS } = DATA_GRID_CONSTANTS;

export const MAX_STATEMENT_FILE_SIZE = 209715200;
export const MAX_STATEMENT_NAME_LENGTH = 100;
export const PDF_UPLOAD_ACCESS = 'application/pdf';
export const SUPPORTED_STATEMENT_FILE_TYPES = {
  'text/html': ['.htm', '.html'],
  'application/msword': ['.doc'],
  'application/vnd.ms-word': ['.doc'], // Alternative MIME type for .doc files
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [
    '.docx',
  ],
  [PDF_UPLOAD_ACCESS]: ['.pdf'],
};

export const getStatementSupportedFileTypes = ({ pdfUploadEnabled }) => {
  if (!pdfUploadEnabled) {
    // Create a copy and remove PDF support
    const { [PDF_UPLOAD_ACCESS]: removed, ...fileTypesWithoutPdf } =
      SUPPORTED_STATEMENT_FILE_TYPES;
    return fileTypesWithoutPdf;
  }
  return SUPPORTED_STATEMENT_FILE_TYPES;
};
export const AUTO_TIEOUT_WORKPAPER_TYPE = 'AUTO_TIEOUT';
export const AUTO_TIEOUT_TB_WORKPAPER_TYPE = 'AUTO_TIEOUT_TB';

export const LEGAL_HOLD_TOOLTIP = new TooltipOptions({
  text: 'project-creation.project-info.legal-hold-verbiage',
  id: 'project-creation-id-tooltip-legal-hold',
  position: 'right',
});

export const ENABLE_COMFORT_LETTER_TOOLTIP = new TooltipOptions({
  text: 'project-creation.project-info.enable-comfort-letter-verbiage.tooltip',
  id: 'project-creation-id-enable-comfort-letter',
  position: 'right',
});

export const AUTO_IR_TOOLTIP = new TooltipOptions({
  text: 'statement-create.statement-info.auto-ir-verbiage',
  id: 'statement-create-statement-info-auto-ir',
  position: 'right',
});

export const STATEMENT_TYPE_ID_MAP = {
  TEN_K: 1, // 10-K
  TEN_Q: 2, // 10-Q
  SIX_K: 5, // 6-K
  PRESS_RELEASE: 6, // 8-K
  TWENTY_F: 7, // 20-F
  FORTY_F: 8, // 40-F
  NCSR: 9, // N-CSR
  PRIVATE_ANNUAL: 11, // Private Annual
  PRIVATE_QUARTERLY: 12, // Private Quarterly
  PROXY_STATEMENT: 13,
  COMFORT_LETTER: 14,
  REGISTRATION_STATEMENT: 15,
  INSURANCE_STATEMENT: 16,
  IM_FUND_REPORT: 17,
  AGENCY_REPORT: 18,
  OTHER: 999, // Other
  GLOBAL_ANNUAL: 19, // Global Annual
  GLOBAL_QUARTERLY: 20, // Global Quarterly
  Label_Non_US_Filers: 'Non-US Filers', // Non-US Filers label
  Label_US_Filers: 'US Filers', // US Filers label
};

const DATE_FORMATS_TITLES_LIST = {
  1: 'statement-create.statement-info.dateFormat.options.d-m-y',
  2: 'statement-create.statement-info.dateFormat.options.m-d-y',
  3: 'statement-create.statement-info.dateFormat.options.y-m-d',
  4: 'statement-create.statement-info.dateFormat.options.y-d-m',
};

export const STATEMENT_TYPE_OPTIONS_MAP = new Map();
STATEMENT_TYPE_OPTIONS_MAP.set(
  STATEMENT_TYPE_ID_MAP.Label_Non_US_Filers,
  new MenuOption({
    id: 'statement-type-label-non-US-filers',
    title: 'statement-create.statement-info.statement-type.label.non-us.filers',
    value: 'statement-type-label-non-US-filers',
    isIntl: true,
    isLabel: true,
  }),
);
STATEMENT_TYPE_OPTIONS_MAP.set(
  STATEMENT_TYPE_ID_MAP.GLOBAL_ANNUAL,
  new MenuOption({
    id: 'statement-type-option-global-annual',
    title:
      'statement-create.statement-info.statement-type.option.global-annual',
    value: STATEMENT_TYPE_ID_MAP.GLOBAL_ANNUAL,
  }),
);
STATEMENT_TYPE_OPTIONS_MAP.set(
  STATEMENT_TYPE_ID_MAP.GLOBAL_QUARTERLY,
  new MenuOption({
    id: 'statement-type-option-global-quarterly',
    title:
      'statement-create.statement-info.statement-type.option.global-quarterly',
    value: STATEMENT_TYPE_ID_MAP.GLOBAL_QUARTERLY,
  }),
);
STATEMENT_TYPE_OPTIONS_MAP.set(
  STATEMENT_TYPE_ID_MAP.Label_US_Filers,
  new MenuOption({
    id: 'statement-type-label-US-filers',
    title: 'statement-create.statement-info.statement-type.label.us.filers',
    value: 'statement-type-label-US-filers',
    isIntl: true,
    isLabel: true,
  }),
);
STATEMENT_TYPE_OPTIONS_MAP.set(
  STATEMENT_TYPE_ID_MAP.TEN_K,
  new MenuOption({
    id: 'statement-type-option-10-K',
    title: 'statement-create.statement-info.statement-type.option.10-K',
    value: STATEMENT_TYPE_ID_MAP.TEN_K,
  }),
);
STATEMENT_TYPE_OPTIONS_MAP.set(
  STATEMENT_TYPE_ID_MAP.TEN_Q,
  new MenuOption({
    id: 'statement-type-option-10-Q',
    title: 'statement-create.statement-info.statement-type.option.10-Q',
    value: STATEMENT_TYPE_ID_MAP.TEN_Q,
  }),
);
STATEMENT_TYPE_OPTIONS_MAP.set(
  STATEMENT_TYPE_ID_MAP.PRIVATE_ANNUAL,
  new MenuOption({
    id: 'statement-type-option-private-annual',
    title:
      'statement-create.statement-info.statement-type.option.private-annual',
    value: STATEMENT_TYPE_ID_MAP.PRIVATE_ANNUAL,
  }),
);
STATEMENT_TYPE_OPTIONS_MAP.set(
  STATEMENT_TYPE_ID_MAP.PRIVATE_QUARTERLY,
  new MenuOption({
    id: 'statement-type-option-private-quarterly',
    title:
      'statement-create.statement-info.statement-type.option.private-quarterly',
    value: STATEMENT_TYPE_ID_MAP.PRIVATE_QUARTERLY,
  }),
);

STATEMENT_TYPE_OPTIONS_MAP.set(
  STATEMENT_TYPE_ID_MAP.IM_FUND_REPORT,
  new MenuOption({
    id: 'statement-type-option-im-fund-report',
    title:
      'statement-create.statement-info.statement-type.option.im-fund-report',
    value: STATEMENT_TYPE_ID_MAP.IM_FUND_REPORT,
  }),
);

STATEMENT_TYPE_OPTIONS_MAP.set(
  STATEMENT_TYPE_ID_MAP.PRESS_RELEASE,
  new MenuOption({
    id: 'statement-type-option-press-release',
    title:
      'statement-create.statement-info.statement-type.option.press-release',
    value: STATEMENT_TYPE_ID_MAP.PRESS_RELEASE,
  }),
);

STATEMENT_TYPE_OPTIONS_MAP.set(
  STATEMENT_TYPE_ID_MAP.AGENCY_REPORT,
  new MenuOption({
    id: 'statement-type-option-agency-report',
    title:
      'statement-create.statement-info.statement-type.option.agency-report',
    value: STATEMENT_TYPE_ID_MAP.AGENCY_REPORT,
  }),
);

STATEMENT_TYPE_OPTIONS_MAP.set(
  STATEMENT_TYPE_ID_MAP.PROXY_STATEMENT,
  new MenuOption({
    id: 'statement-type-option-proxy-statement',
    title:
      'statement-create.statement-info.statement-type.option.proxy-statement',
    value: STATEMENT_TYPE_ID_MAP.PROXY_STATEMENT,
  }),
);
STATEMENT_TYPE_OPTIONS_MAP.set(
  STATEMENT_TYPE_ID_MAP.COMFORT_LETTER,
  new MenuOption({
    id: 'statement-type-option-comfort-letter',
    title:
      'statement-create.statement-info.statement-type.option.comfort-letter',
    value: STATEMENT_TYPE_ID_MAP.COMFORT_LETTER,
  }),
);
STATEMENT_TYPE_OPTIONS_MAP.set(
  STATEMENT_TYPE_ID_MAP.REGISTRATION_STATEMENT,
  new MenuOption({
    id: 'statement-type-option-registration-statement',
    title:
      'statement-create.statement-info.statement-type.option.registration-statement',
    value: STATEMENT_TYPE_ID_MAP.REGISTRATION_STATEMENT,
  }),
);
STATEMENT_TYPE_OPTIONS_MAP.set(
  STATEMENT_TYPE_ID_MAP.INSURANCE_STATEMENT,
  new MenuOption({
    id: 'statement-type-option-stat-audit-report',
    title:
      'statement-create.statement-info.statement-type.option.insurance-statement',
    value: STATEMENT_TYPE_ID_MAP.INSURANCE_STATEMENT,
  }),
);
STATEMENT_TYPE_OPTIONS_MAP.set(
  STATEMENT_TYPE_ID_MAP.SIX_K,
  new MenuOption({
    id: 'statement-type-option-6-K',
    title: 'statement-create.statement-info.statement-type.option.6-K',
    value: STATEMENT_TYPE_ID_MAP.SIX_K,
  }),
);
STATEMENT_TYPE_OPTIONS_MAP.set(
  STATEMENT_TYPE_ID_MAP.TWENTY_F,
  new MenuOption({
    id: 'statement-type-option-20-F',
    title: 'statement-create.statement-info.statement-type.option.20-F',
    value: STATEMENT_TYPE_ID_MAP.TWENTY_F,
  }),
);
STATEMENT_TYPE_OPTIONS_MAP.set(
  STATEMENT_TYPE_ID_MAP.FORTY_F,
  new MenuOption({
    id: 'statement-type-option-40-F',
    title: 'statement-create.statement-info.statement-type.option.40-F',
    value: STATEMENT_TYPE_ID_MAP.FORTY_F,
  }),
);
STATEMENT_TYPE_OPTIONS_MAP.set(
  STATEMENT_TYPE_ID_MAP.NCSR,
  new MenuOption({
    id: 'statement-type-option-N-CSR',
    title: 'statement-create.statement-info.statement-type.option.N-CSR',
    value: STATEMENT_TYPE_ID_MAP.NCSR,
  }),
);

STATEMENT_TYPE_OPTIONS_MAP.set(
  STATEMENT_TYPE_ID_MAP.OTHER,
  new MenuOption({
    id: 'statement-type-option-other',
    title: 'statement-create.statement-info.statement-type.option.other',
    value: STATEMENT_TYPE_ID_MAP.OTHER,
  }),
);

export const STATEMENT_TYPE_OPTIONS = Array.from(
  STATEMENT_TYPE_OPTIONS_MAP.values(),
);

export const QUARTER_OPTIONS_MAP = new Map();
QUARTER_OPTIONS_MAP.set(
  'Q1',
  new MenuOption({
    id: 'quarter-option-q1',
    title: 'statement-create.statement-info.quarter.option.q1',
    value: 1,
  }),
);
QUARTER_OPTIONS_MAP.set(
  'Q2',
  new MenuOption({
    id: 'quarter-option-q2',
    title: 'statement-create.statement-info.quarter.option.q2',
    value: 2,
  }),
);
QUARTER_OPTIONS_MAP.set(
  'Q3',
  new MenuOption({
    id: 'quarter-option-q3',
    title: 'statement-create.statement-info.quarter.option.q3',
    value: 3,
  }),
);

export const QUARTER_OPTIONS = Array.from(QUARTER_OPTIONS_MAP.values());

const FISCAL_YEAR_PREVIOUS_YEARS_DEFAULT = 5;
const FISCAL_YEAR_NEXT_YEARS_DEFAULT = 2;

export const getFiscalYearPreviousCount = () => {
  const envValue = parseInt(window?.TIEOUT?.ENV?.FISCAL_YEAR_PREVIOUS_YEARS, 10);
  return Number.isFinite(envValue) && envValue >= 0
    ? envValue
    : FISCAL_YEAR_PREVIOUS_YEARS_DEFAULT;
};

export const getFiscalYearNextCount = () => {
  const envValue = parseInt(window?.TIEOUT?.ENV?.FISCAL_YEAR_NEXT_YEARS, 10);
  return Number.isFinite(envValue) && envValue >= 0
    ? envValue
    : FISCAL_YEAR_NEXT_YEARS_DEFAULT;
};

export const generateFiscalYearOptionsMap = (previousYears, nextYears) => {
  const map = new Map();
  const totalYears = previousYears + 1 + nextYears; // previous + current + next

  for (let i = 0; i < totalYears; i++) {
    const offset = i - previousYears;
    const year = moment().add(offset, 'y').format(DATE_FORMATS.YEAR);
    map.set(
      year,
      new MenuOption({
        id: `fiscal-year-option-${i + 1}`,
        title: year,
        value: year,
        isIntl: false,
      }),
    );
  }
  return map;
};

export const FISCAL_YEAR_OPTIONS_MAP = generateFiscalYearOptionsMap(
  getFiscalYearPreviousCount(),
  getFiscalYearNextCount(),
);

export const FISCAL_YEAR_OPTIONS = Array.from(FISCAL_YEAR_OPTIONS_MAP.values());

const ACCOUNTING_BASIS_TYPES = {
  GAAP: 'GAAP',
  IFRS: 'IFRS',
  STATUTORY: 'Statutory',
  NOT_APPLICABLE: 'Not applicable',
};

export const ACCOUNTING_BASIS_OPTIONS_MAP = new Map(
  Object.entries({
    [ACCOUNTING_BASIS_TYPES.GAAP]: new MenuOption({
      id: 'accounting-basis-option-GAAP',
      title: 'statement-create.statement-info.accounting-basis.option.GAAP',
      value: ACCOUNTING_BASIS_TYPES.GAAP,
    }),
    [ACCOUNTING_BASIS_TYPES.IFRS]: new MenuOption({
      id: 'accounting-basis-option-IFRS',
      title: 'statement-create.statement-info.accounting-basis.option.IFRS',
      value: ACCOUNTING_BASIS_TYPES.IFRS,
    }),
    [ACCOUNTING_BASIS_TYPES.STATUTORY]: new MenuOption({
      id: 'accounting-basis-option-STATUTORY',
      title:
        'statement-create.statement-info.accounting-basis.option.STATUTORY',
      value: ACCOUNTING_BASIS_TYPES.STATUTORY,
    }),
    [ACCOUNTING_BASIS_TYPES.NOT_APPLICABLE]: new MenuOption({
      id: 'accounting-basis-option-other',
      title: 'statement-create.statement-info.accounting-basis.option.NA',
      value: ACCOUNTING_BASIS_TYPES.NOT_APPLICABLE,
    }),
  }),
);

export const ACCOUNTING_BASIS_OPTIONS = Array.from(
  ACCOUNTING_BASIS_OPTIONS_MAP.values(),
);

const REGISTRANT_TYPES = {
  SEC: 'SEC',
  NAIC: 'NAIC',
  OTHER: 'Other',
};

export const REGISTRANT_OPTIONS_MAP = new Map();
REGISTRANT_OPTIONS_MAP.set(
  REGISTRANT_TYPES.SEC,
  new MenuOption({
    id: 'registrant-option-SEC',
    title: 'statement-create.statement-info.registrant.option.SEC',
    value: REGISTRANT_TYPES.SEC,
  }),
);
REGISTRANT_OPTIONS_MAP.set(
  REGISTRANT_TYPES.NAIC,
  new MenuOption({
    id: 'registrant-option-NAIC',
    title: 'statement-create.statement-info.registrant.option.NAIC',
    value: REGISTRANT_TYPES.NAIC,
  }),
);
REGISTRANT_OPTIONS_MAP.set(
  REGISTRANT_TYPES.OTHER,
  new MenuOption({
    id: 'registrant-option-other',
    title: 'statement-create.statement-info.registrant.option.other',
    value: REGISTRANT_TYPES.OTHER,
  }),
);

export const REGISTRANT_OPTIONS = Array.from(REGISTRANT_OPTIONS_MAP.values());

export const SCALING_TYPES = {
  ONES: 1,
  THOUSANDS: 2,
  LAKHS: 7,
  CRORES: 8,
  MILLIONS: 3,
  BILLIONS: 4,
  TRILLIONS: 5,
  OTHER: 6,
};

export const SCALING_OPTIONS_MAP = new Map();
SCALING_OPTIONS_MAP.set(
  SCALING_TYPES.THOUSANDS,
  new MenuOption({
    id: 'scaling-option-thousand',
    title: 'statement-create.statement-info.scaling.option.thousand',
    value: SCALING_TYPES.THOUSANDS,
  }),
);
SCALING_OPTIONS_MAP.set(
  SCALING_TYPES.ONES,
  new MenuOption({
    id: 'scaling-option-ones',
    title: 'statement-create.statement-info.scaling.option.ones',
    value: SCALING_TYPES.ONES,
  }),
);
SCALING_OPTIONS_MAP.set(
  SCALING_TYPES.LAKHS,
  new MenuOption({
    id: 'scaling-option-lakhs',
    title: 'statement-create.statement-info.scaling.option.lakhs',
    value: SCALING_TYPES.LAKHS,
  }),
);
SCALING_OPTIONS_MAP.set(
  SCALING_TYPES.CRORES,
  new MenuOption({
    id: 'scaling-option-crores',
    title: 'statement-create.statement-info.scaling.option.crores',
    value: SCALING_TYPES.CRORES,
  }),
);
SCALING_OPTIONS_MAP.set(
  SCALING_TYPES.MILLIONS,
  new MenuOption({
    id: 'scaling-option-million',
    title: 'statement-create.statement-info.scaling.option.million',
    value: SCALING_TYPES.MILLIONS,
  }),
);
SCALING_OPTIONS_MAP.set(
  SCALING_TYPES.BILLIONS,
  new MenuOption({
    id: 'scaling-option-billion',
    title: 'statement-create.statement-info.scaling.option.billion',
    value: SCALING_TYPES.BILLIONS,
  }),
);
SCALING_OPTIONS_MAP.set(
  SCALING_TYPES.TRILLIONS,
  new MenuOption({
    id: 'scaling-option-trillion',
    title: 'statement-create.statement-info.scaling.option.trillion',
    value: SCALING_TYPES.TRILLIONS,
  }),
);
SCALING_OPTIONS_MAP.set(
  SCALING_TYPES.OTHER,
  new MenuOption({
    id: 'scaling-option-other',
    title: 'statement-create.statement-info.scaling.option.other',
    value: SCALING_TYPES.OTHER,
  }),
);

export const SCALING_OPTIONS = Array.from(SCALING_OPTIONS_MAP.values());

export const TRIAL_BALANCE_SCALING_OPTIONS_MAP = new Map();
TRIAL_BALANCE_SCALING_OPTIONS_MAP.set(
  SCALING_TYPES.ONES,
  new MenuOption({
    id: 'scaling-option-ones',
    title: 'statement-create.statement-info.scaling.option.ones',
    value: SCALING_TYPES.ONES,
  }),
);
TRIAL_BALANCE_SCALING_OPTIONS_MAP.set(
  SCALING_TYPES.THOUSANDS,
  new MenuOption({
    id: 'scaling-option-thousand',
    title: 'statement-create.statement-info.scaling.option.thousand',
    value: SCALING_TYPES.THOUSANDS,
  }),
);
TRIAL_BALANCE_SCALING_OPTIONS_MAP.set(
  SCALING_TYPES.LAKHS,
  new MenuOption({
    id: 'scaling-option-lakhs',
    title: 'statement-create.statement-info.scaling.option.lakhs',
    value: SCALING_TYPES.LAKHS,
  }),
);
TRIAL_BALANCE_SCALING_OPTIONS_MAP.set(
  SCALING_TYPES.CRORES,
  new MenuOption({
    id: 'scaling-option-crores',
    title: 'statement-create.statement-info.scaling.option.crores',
    value: SCALING_TYPES.CRORES,
  }),
);
TRIAL_BALANCE_SCALING_OPTIONS_MAP.set(
  SCALING_TYPES.MILLIONS,
  new MenuOption({
    id: 'scaling-option-million',
    title: 'statement-create.statement-info.scaling.option.million',
    value: SCALING_TYPES.MILLIONS,
  }),
);
TRIAL_BALANCE_SCALING_OPTIONS_MAP.set(
  SCALING_TYPES.BILLIONS,
  new MenuOption({
    id: 'scaling-option-billion',
    title: 'statement-create.statement-info.scaling.option.billion',
    value: SCALING_TYPES.BILLIONS,
  }),
);
TRIAL_BALANCE_SCALING_OPTIONS_MAP.set(
  SCALING_TYPES.TRILLIONS,
  new MenuOption({
    id: 'scaling-option-trillion',
    title: 'statement-create.statement-info.scaling.option.trillion',
    value: SCALING_TYPES.TRILLIONS,
  }),
);
export const TRIAL_BALANCE_SCALING_OPTIONS = Array.from(
  TRIAL_BALANCE_SCALING_OPTIONS_MAP.values(),
);

export const TB_PENDING_STEPS = {
  NONE: '',
  FETCH_UPDATED_SNAPSHOT: 'FETCH_UPDATED_SNAPSHOT',
  INITIATE_TB_AUTO_TIEOUT: 'INITIATE_TB_AUTO_TIEOUT',
};

const UNIT_TYPES = {
  CURRENCY: 1,
  PERCENTAGE: 2,
  SHARES: 3,
  TEXT: 4,
  OTHER: 5,
};

export const UNIT_OPTIONS_MAP = new Map();
UNIT_OPTIONS_MAP.set(
  UNIT_TYPES.CURRENCY,
  new MenuOption({
    id: 'unit-option-currency',
    title: 'statement-create.statement-info.unit.option.currency',
    value: UNIT_TYPES.CURRENCY,
  }),
);
UNIT_OPTIONS_MAP.set(
  UNIT_TYPES.PERCENTAGE,
  new MenuOption({
    id: 'unit-option-percentage',
    title: 'statement-create.statement-info.unit.option.percentage',
    value: UNIT_TYPES.PERCENTAGE,
  }),
);
UNIT_OPTIONS_MAP.set(
  UNIT_TYPES.SHARES,
  new MenuOption({
    id: 'unit-option-shares',
    title: 'statement-create.statement-info.unit.option.shares',
    value: UNIT_TYPES.SHARES,
  }),
);
UNIT_OPTIONS_MAP.set(
  UNIT_TYPES.TEXT,
  new MenuOption({
    id: 'unit-option-text',
    title: 'statement-create.statement-info.unit.option.text',
    value: UNIT_TYPES.TEXT,
  }),
);
UNIT_OPTIONS_MAP.set(
  UNIT_TYPES.OTHER,
  new MenuOption({
    id: 'unit-option-other',
    title: 'statement-create.statement-info.unit.option.other',
    value: UNIT_TYPES.OTHER,
  }),
);

export const UNIT_OPTIONS = Array.from(UNIT_OPTIONS_MAP.values());

const FORMULA_ORIENTATION_TYPES = {
  TOTALS_AT_TOP: 1,
  TOTALS_AT_BOTTOM: 2,
};

export const FORMULA_ORIENTATION_OPTIONS_MAP = new Map();
FORMULA_ORIENTATION_OPTIONS_MAP.set(
  FORMULA_ORIENTATION_TYPES.TOTALS_AT_TOP,
  new MenuOption({
    id: 'formula-orientation-totals-at-top',
    title:
      'statement-create.statement-info.formula-orientation.option.totals-at-top',
    value: FORMULA_ORIENTATION_TYPES.TOTALS_AT_TOP,
  }),
);
FORMULA_ORIENTATION_OPTIONS_MAP.set(
  FORMULA_ORIENTATION_TYPES.TOTALS_AT_BOTTOM,
  new MenuOption({
    id: 'formula-orientation-totals-at-bottom',
    title:
      'statement-create.statement-info.formula-orientation.option.totals-at-bottom',
    value: FORMULA_ORIENTATION_TYPES.TOTALS_AT_BOTTOM,
  }),
);

export const FORMULA_ORIENTATION_OPTIONS = Array.from(
  FORMULA_ORIENTATION_OPTIONS_MAP.values(),
);

export const getProjectNameMenuOptions = (projectList, sort = false) => {
  let list = Array.isArray(projectList) ? [...projectList] : [];
  if (sort) {
    list.sort((a, b) => {
      if (!a.name || !b.name) return 0;
      return a.name
        .trim()
        .toLowerCase()
        .localeCompare(b.name.trim().toLowerCase());
    });
  }
  return list.map(
    (project) =>
      new MenuOption({
        id: project.id,
        title: project.name,
        value: project,
        isIntl: false,
      }),
  );
};

const filterLanguagesForLegacy = [
  'sd-Arab', // Sindhi
  'zgh-Tfng', // Standard Moroccan Tamazight (tamazightTfng)
  'zgh-Tfng-MA', // Standard Moroccan Tamazight (tamazightTfngMA)
  'nqo-GN', // N'Ko (nqo-GN)
  'iu-Cans', // Inuktitut (iu-Cans) - inuktitutSyllabics
  'iu-Cans-CA', // Inuktitut (iu-CansCA) - inuktitutSyllabicsCA
  'km-KH', // Khmer (km-KH)
  'lo-LA', // Lao (lo-LA)
  'my-MM', // Burmese (my-MM)
  'tzm-Tfng-MA', // Central Atlas Tamazight (tzm-TfngMA)
  'chr-Cher', // Cherokee (chr-Cher)
  'mn-Cyrl', //'Mongolian (Cyrillic)',
  'mn-Cyrl-MN', //'Mongolian (Cyrillic, Mongolia)',
  'mn-Mong', //'Mongolian (Traditional Mongolian)',
  'mn-Mong-CN', //'Mongolian (Traditional Mongolian, China)',
  'mn-Mong-MN', //'Mongolian (Traditional Mongolian, Mongolia)',
  'tzm-Tfng-MA', //'Central Atlas Tamazight (Tifinagh, Morocco)'
  'ti-ER', //'Tigrinya (Eritrea)'
  'ti-ET', //'Tigrinya (Ethiopia)'
  'bo-CN', //'Tibetan (China)'
];

export const getLanguagesMenuOptions = (languages, isPdfSelected) => {
  const languageOptions = languages.reduce(
    (acc, lang) => {
      const option = new MenuOption({
        id: lang.languageCode,
        title: lang.languageName,
        value: lang.languageCode,
        isIntl: false,
      });
      lang.displayOrder >= 0
        ? acc.main.push(option)
        : !isPdfSelected &&
          !filterLanguagesForLegacy.includes(lang.languageCode)
          ? acc.other.push(option)
          : isPdfSelected && acc.other.push(option);
      return acc;
    },
    { main: [], other: [] },
  );

  languageOptions.main.push(getLanguageOtherOption());
  return [...languageOptions.main, ...languageOptions.other];
};

const getLanguageOtherOption = () => {
  return new MenuOption({
    id: 'statement-create.statement-info.language-other-option',
    title: 'statement-create.statement-info.language-other-option.label',
    value: 'statement-create.statement-info.language-other-option',
    isIntl: true,
    isNotIntl: false,
    isLabel: true,
  });
};

export const defaultSelectedProject = (selectedProject) => {
  return new MenuOption({
    id: selectedProject.id,
    title: selectedProject.name,
    value: selectedProject,
    isIntl: false,
  });
};

/*
 * Check if the uploaded file is of DOCX, DOC, HTM or HTML type
 */
export const isUploadedFileIsOfDocxOrHtmlType = (file) => {
  if (!file) return false;

  const fileName = file.name?.toLowerCase();
  if (!fileName) return false;

  const validExtensions = ['.docx', '.doc', '.htm', '.html'];
  const hasValidExtension = validExtensions.some((ext) =>
    fileName.endsWith(ext),
  );
  return hasValidExtension;
};

export const carryforwardColumns = (statementInProgress) => [
  new DataGridCheckboxColumn({
    checkboxId: 'carryforward-col-checkbox-id',
    tooltipConfig: ({ disabled }) => {
      if (disabled) {
        return new TooltipOptions({
          text: 'statement-create.carry-forward.statement-list.checkbox.tooltip.message',
          id: 'carryforward-col-checkbox-tooltip-id',
          position: 'right',
        });
      }
    },
    disabled: ({ rowData, colModel }) => {
      const isUploadedFileIsDocxOrHtml = isUploadedFileIsOfDocxOrHtmlType(
        statementInProgress?.files?.[0],
      );
      return (
        colModel.key === SPECIAL_COLUMNS.checkbox &&
        isUploadedFileIsDocxOrHtml &&
        rowData.documentProcessType === 2
      );
    },
  }),
  new DataGridColumn({
    key: 'statementName',
    header: 'statement-list.column.statement-name',
    width: '40%',
    formatter: (statement, { rowKey }) => {
      const getStatementStatusSuffix = () => {
        if (statement.isPendingApproval()) {
          return (
            <FormattedMessage
              key="pending-approval"
              id="statement-list.column.statement-name.suffix.status.pending-approval"
            />
          );
        } else if (statement.hasApprovalInitiated()) {
          return (
            <FormattedMessage
              key="approval-initiated"
              id="statement-list.column.statement-name.suffix.status.approval-initiated"
            />
          );
        } else if (statement.isWrappedUp()) {
          return (
            <FormattedMessage
              key="wrapped-up"
              id="statement-list.column.statement-name.suffix.status.wrapped-up"
            />
          );
        }
      };
      const colId = `statement-name-col-${rowKey}`;
      return (
        <>
          <MultilineEllipsis
            toolTipID={colId}
            text={statement.statementName}
            isNotInternationalized
          />
          {getStatementStatusSuffix()}
        </>
      );
    },
  }),
  new DataGridColumn({
    key: 'type',
    header: 'statement-list.column.type',
    width: '15%',
    formatter: (statement, { rowKey }) => {
      const colId = `statement-type-col-${rowKey}`;
      return (
        <MultilineEllipsis
          toolTipID={colId}
          text={statement.type}
          isNotInternationalized
        />
      );
    },
  }),
  new DataGridColumn({
    key: 'fiscalYear',
    header: 'statement-list.column.fiscal-year',
    width: '15%',
  }),
  new DataGridColumn({
    key: 'periodEnding',
    header: 'statement-list.column.period-ending',
    width: '15%',
    formatter: (statement) =>
      moment(statement.periodEnding).format(DATE_FORMATS.MONTH_DAY_YEAR),
  }),
  new DataGridColumn({
    key: 'status',
    header: 'statement-list.column.status',
    width: '15%',
    formatter: (statement) => {
      return `${Math.round(
        (statement.percentageCompleted * 100 * 100) / 100,
      )}%`;
    },
  }),
];
export const ENABLE_LEGAL_HOLD_TOOLTIP = new TooltipOptions({
  text: 'project-edit.manage-legal-hold-enable-tooltip.text',
  id: 'project-edit-manage-legal-hold-enable-tool-tip',
  position: 'right',
});
export const DISABLE_LEGAL_HOLD_TOOLTIP = new TooltipOptions({
  text: 'project-edit.manage-legal-hold-disable-tooltip.text',
  id: 'project-edit-manage-legal-hold-disable-tool-tip',
  position: 'right',
});
export const ENABLE_AUTO_IR_TOOLTIP = new TooltipOptions({
  text: 'statement-create.statement.info-auto-ir-enable-tooltip.text',
  id: 'statement-create-statement-info-auto-ir-enable-tool-tip',
  position: 'right',
});

export const DISABLED_COMFORT_LETTER_TOOLTIP = new TooltipOptions({
  text: 'project-edit.disabled-comfort-letter-tooltip.text',
  id: 'project-edit-disabled-comfort-letter-tooltip-id',
  position: 'right',
});

export const getDateFormatMenuOptions = (dateFormats) => {
  return dateFormats.map(
    (date) =>
      new MenuOption({
        id: date.id,
        title: DATE_FORMATS_TITLES_LIST[date.id],
        value: date.id,
        isIntl: true,
      }),
  );
};

export const getNumberFormatMenuOptions = (numberFormats) => {
  return numberFormats.map(
    (numb) =>
      new MenuOption({
        id: numb.id,
        title: numb.formatKey.trim(),
        value: numb.id,
        isIntl: true,
      }),
  );
};

export const getCurrencyMenuOptions = (currency) => {
  return currency.map(
    (cur) =>
      new MenuOption({
        id: cur.id,
        title: `statement-edit.statement-info.currency.option.${cur.currencyCode}`,
        value: cur.id,
        isIntl: true,
      }),
  );
};

export const defaultCurrency = new MenuOption({
  id: 1,
  value: 1,
  title: 'statement-edit.statement-info.currency.option.USD',
  isIntl: true,
});
